import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';
import type {
  FinancialReportsModels,
  GeneralLedgerModels,
  BalanceSheetTempModels,
} from './FinancialReportsModels';

export const getIncomeStatement = createAsyncThunk(
  'FinancialReports/getIncomeStatement',
  async (
    {
      startDate,
      endDate,
      accountlevel,
    }: { startDate?: string; endDate?: string; accountlevel?: string },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await ntxAPI.get('Reports/GetIncomeStatement', {
        params: {
          ...(startDate && { StartDate: startDate }),
          ...(endDate && { EndDate: endDate }),
          ...(accountlevel && { Accountlevel: accountlevel }),
        },
      });
      if (data?.succeeded) {
        return data?.data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getBalanceSheet = createAsyncThunk(
  'FinancialReports/getBalanceSheet',
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get('Reports/GetBalanceSheet', {
        params: {
          StartDate: startDate,
          EndDate: endDate,
        },
      });
      if (data?.succeeded) {
        return data?.data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getGeneralLedger = createAsyncThunk(
  'FinancialReports/getGeneralLedger',
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get('Reports/GetGeneralLedger', {
        params: {
          StartDate: startDate,
          EndDate: endDate,
        },
      });
      if (data?.succeeded) {
        return data?.data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getBalanceSheetTemp = createAsyncThunk(
  'FinancialReports/getBalanceSheetTemp',
  async (
    {
      startDate,
      endDate,
      accountlevel,
    }: { startDate?: string; endDate?: string; accountlevel?: string },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await ntxAPI.get('Reports/GetBalanceSheetTemp', {
        params: {
          ...(startDate && { StartDate: startDate }),
          ...(endDate && { EndDate: endDate }),
          ...(accountlevel && { Accountlevel: accountlevel }),
        },
      });
      if (data?.succeeded) {
        // console.log(JSON.stringify(data?.data));

        return data?.data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState: {
  IncomeStatement: FinancialReportsModels; // قائمة
  BalanceSheet: FinancialReportsModels;
  GeneralLedger: GeneralLedgerModels[];
  BalanceSheetTemp: BalanceSheetTempModels; //ميزان المراجعة
  loading: boolean;
  error: any | null;
} = {
  IncomeStatement: {} as FinancialReportsModels, // قائمة
  BalanceSheet: {} as FinancialReportsModels,
  GeneralLedger: [],
  BalanceSheetTemp: {} as BalanceSheetTempModels, //ميزان المراجعة
  loading: false,
  error: null,
};

const FinancialReportsSlice = createSlice({
  name: 'FinancialReports',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder

      .addCase(getIncomeStatement.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        getIncomeStatement.fulfilled,
        (state, action: PayloadAction<FinancialReportsModels>) => {
          state.loading = false;
          state.IncomeStatement = action.payload;
        }
      )
      .addCase(getIncomeStatement.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      //~~~~~~~~~~~~

      .addCase(getBalanceSheet.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        getBalanceSheet.fulfilled,
        (state, action: PayloadAction<FinancialReportsModels>) => {
          state.loading = false;
          state.BalanceSheet = action.payload;
        }
      )
      .addCase(getBalanceSheet.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      //~~~~~~~~~~~~

      .addCase(getGeneralLedger.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        getGeneralLedger.fulfilled,
        (state, action: PayloadAction<GeneralLedgerModels[]>) => {
          state.loading = false;
          state.GeneralLedger = action.payload;
        }
      )
      .addCase(getGeneralLedger.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      //~~~~~~~~~~~~
      //~~~~~~~~~~~~

      .addCase(getBalanceSheetTemp.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        getBalanceSheetTemp.fulfilled,
        (state, action: PayloadAction<BalanceSheetTempModels>) => {
          state.loading = false;
          state.BalanceSheetTemp = action.payload;
        }
      )
      .addCase(getBalanceSheetTemp.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
    //~~~~~~~~~~~~
  },
});

export default FinancialReportsSlice.reducer;
